import React from 'react';
import DownloadService from './DownloadService';

const DownloadModal = ({ 
  isOpen,
  onClose, 
  title = 'Download Data',
  data = [], 
  columns = [], 
  selectedAC = null,
  selectedACName = null,
  selectedBooth = null,
  isLoading = false,
  setIsLoading,
  screenName = 'Report'
}) => {
  const getFilePrefix = () => {
    let prefix = '';
    
    // Add AC number and name if available
    if (selectedAC) {
      prefix += `ac${selectedAC}_`;
      
      // Add AC name if available
      if (selectedACName) {
        // Replace spaces with underscores for file names
        const formattedACName = selectedACName.replace(/\s+/g, '_');
        prefix += `${formattedACName}_`;
      }
    }
    
    // Add booth number if available
    if (selectedBooth) {
      prefix += `booth${selectedBooth}_`;
    }
    
    // Use a default prefix if nothing is available
    if (!prefix) {
      prefix = 'data_';
    }
    
    return prefix;
  };

  const handleDownloadCSV = async () => {
    setIsLoading(true);
    
    const csvContent = DownloadService.generateCSVContent({
      columns,
      data
    });
    
    await DownloadService.downloadCSV({
      csvContent,
      filePrefix: getFilePrefix(),
      screenName: screenName,
      onStart: () => setIsLoading(true),
      onComplete: () => {
        setIsLoading(false);
        onClose();
      },
      onError: () => setIsLoading(false)
    });
  };

  const handleDownloadXML = async () => {
    setIsLoading(true);
    
    // Create mapping for XML elements
    const mapping = {};
    if (columns.length > 0) {
      // Add index element if first column is an index
      if (columns[0].value === 'index') {
        mapping.index = 'SerialNo';
      }
      
      // Map other columns
      columns.forEach(col => {
        if (col.value !== 'index') {
          // Convert header to valid XML element name (remove spaces, special chars)
          const xmlElement = col.header
            .replace(/[^a-zA-Z0-9]/g, '')
            .replace(/\s+/g, '');
          mapping[col.value] = xmlElement;
        }
      });
    }
    
    // Create root attributes
    const rootAttributes = {};
    if (selectedAC) {
      rootAttributes.AC = selectedAC;
      if (selectedACName) {
        rootAttributes.ACName = selectedACName;
      }
    }
    if (selectedBooth) {
      rootAttributes.BOOTH = selectedBooth;
    }
    rootAttributes.ScreenName = screenName;
    
    const xmlContent = DownloadService.generateXMLContent({
      rootElement: 'DataExport',
      childElement: 'Item',
      rootAttributes,
      data,
      mapping
    });
    
    await DownloadService.downloadXML({
      xmlContent,
      filePrefix: getFilePrefix(),
      screenName: screenName,
      onStart: () => setIsLoading(true),
      onComplete: () => {
        setIsLoading(false);
        onClose();
      },
      onError: () => setIsLoading(false)
    });
  };

  const handleDownloadPDF = async () => {
    setIsLoading(true);
    
    let documentTitle = title;
    
    // Add screen name to document title if different from title
    if (screenName !== title) {
      documentTitle = `${screenName} - ${documentTitle}`;
    }
    
    // Add AC information to document title
    if (selectedAC) {
      documentTitle += ` - AC: ${selectedAC}`;
      
      if (selectedACName) {
        documentTitle += ` (${selectedACName})`;
      }
    }
    
    // Add booth information to document title
    if (selectedBooth) {
      documentTitle += ` - Booth: ${selectedBooth}`;
    }
    
    await DownloadService.downloadPDF({
      title: documentTitle,
      columns,
      data,
      filePrefix: getFilePrefix(),
      screenName: screenName,
      onStart: () => setIsLoading(true),
      onComplete: () => {
        setIsLoading(false);
        onClose();
      },
      onError: () => setIsLoading(false)
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4 max-h-[50vh] overflow-hidden">
        <div className="border-b border-gray-200">
          <h3 className="text-lg font-semibold text-center py-3 text-blue-900">{title}</h3>
        </div>
        
        {isLoading ? (
          <div className="p-8 flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
            <p className="mt-4 text-blue-900 font-medium">Preparing your download...</p>
          </div>
        ) : (
          <div className="p-4">
            <button 
              onClick={handleDownloadPDF}
              className="flex items-center w-full py-3 px-4 border-b border-gray-100 hover:bg-gray-50"
            >
              <img 
                src="/assets/images/ic_pdf.png" 
                alt="PDF" 
                className="w-8 h-8 mr-3"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiNmZjZlMDAiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cGF0aCBkPSJNMTQgMkg2YTIgMiAwIDAgMC0yIDJ2MTZhMiAyIDAgMCAwIDIgMmgxMmEyIDIgMCAwIDAgMi0yVjh6Ij48L3BhdGg+PHBvbHlsaW5lIHBvaW50cz0iMTQgMiAxNCA4IDIwIDgiPjwvcG9seWxpbmU+PHBhdGggZD0iTTkgMTVoNnYtNUg5djV6Ij48L3BhdGg+PC9zdmc+";
                }}
              />
              <span className="text-gray-800 text-base">Download as PDF</span>
            </button>
            
            <button 
              onClick={handleDownloadCSV}
              className="flex items-center w-full py-3 px-4 border-b border-gray-100 hover:bg-gray-50"
            >
              <img 
                src="/assets/images/ic_csv.png" 
                alt="CSV" 
                className="w-8 h-8 mr-3"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiMwMGIzMDAiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cGF0aCBkPSJNMTQgMkg2YTIgMiAwIDAgMC0yIDJ2MTZhMiAyIDAgMCAwIDIgMmgxMmEyIDIgMCAwIDAgMi0yVjh6Ij48L3BhdGg+PHBvbHlsaW5lIHBvaW50cz0iMTQgMiAxNCA4IDIwIDgiPjwvcG9seWxpbmU+PGxpbmUgeDE9IjgiIHgyPSIxNiIgeTE9IjEzIiB5Mj0iMTMiPjwvbGluZT48bGluZSB4MT0iOCIgeDI9IjE2IiB5MT0iMTciIHkyPSIxNyI+PC9saW5lPjwvc3ZnPg==";
                }}
              />
              <span className="text-gray-800 text-base">Download as CSV</span>
            </button>
            
            <button 
              onClick={handleDownloadXML}
              className="flex items-center w-full py-3 px-4 border-b border-gray-100 hover:bg-gray-50"
            >
              <img 
                src="/assets/images/ic_xml.png" 
                alt="XML" 
                className="w-8 h-8 mr-3"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM0Mjg1ZjQiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cGF0aCBkPSJNMTQgMkg2YTIgMiAwIDAgMC0yIDJ2MTZhMiAyIDAgMCAwIDIgMmgxMmEyIDIgMCAwIDAgMi0yVjh6Ij48L3BhdGg+PHBvbHlsaW5lIHBvaW50cz0iMTQgMiAxNCA4IDIwIDgiPjwvcG9seWxpbmU+PHBhdGggZD0iTTggMTJsMyA0bTAgMGw0LTQiPjwvcGF0aD48L3N2Zz4=";
                }}
              />
              <span className="text-gray-800 text-base">Download as XML</span>
            </button>
            
            <button 
              onClick={onClose}
              className="w-full text-center py-4 text-gray-500 font-semibold hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DownloadModal;